import React, {useEffect, useState} from "react";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast, generateRandomNumber, registeredUpdatedTitle} from "../../utils/utility";
import SlideOver from "../../components/ui/SlideOver";
import TextField from "../../components/ui/TextField";
import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import {ASSET_TYPES, registerAssetBody} from "./assetMeta";
import {COUNTRIES} from "../../component/shared/CountrySelector";
import {VEHICLE_TYPES} from "../../utils/vehicleIcons";

const EMPTY = {
  name: "",
  assetUnitId: "",
  assetType: "",
  description: "",
  assetPassword: "",
  countryCode: "",
  vehicleType: "",
  protocol: "",
};

/**
 * Unified Create / Edit slide-over for assets.
 *   mode: "create" | "edit"
 *   asset: original record (edit mode)
 *   clientId / clientName: the company a new device is registered into (create mode). Defaults to
 *     the signed-in user's own; the super admin console passes the client it is showing.
 */
export default function AssetSlideOver({mode = "edit", asset, open, onClose, onSaved, clientId, clientName}) {
  const [form, setForm] = useState({...EMPTY});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Backend rejection message (e.g. "phone linked to another client") shown inline
  // so it can't be missed if the toast is not noticed.
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && asset) {
      setForm({
        name: asset.name || "",
        assetUnitId: asset.assetUnitId || "",
        assetType: asset.assetType || "",
        description: asset.description || "",
        assetPassword: asset.assetPassword || "",
        countryCode: "",
        vehicleType: asset.vehicleType || "",
        protocol: asset.protocol || "",
      });
    } else {
      setForm({...EMPTY});
    }
    setErrors({});
    setSubmitError("");
  }, [open, mode, asset]);

  const change = (k, v) => {
    setForm((p) => ({...p, [k]: v}));
    if (errors[k]) setErrors((p) => ({...p, [k]: undefined}));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Asset name is required.";
    if (!form.assetUnitId.trim()) next.assetUnitId = "Unit ID is required.";
    if (!form.assetType) next.assetType = "Pick an asset type.";
    if (form.assetType === "GPS_LOCK" && mode === "create" && form.assetPassword.trim().length !== 6)
      next.assetPassword = "Password must be exactly 6 characters.";
    if (form.assetType === "MOBI_TRACK" && mode === "create" && !form.countryCode)
      next.countryCode = "Country code required for Track Point devices.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSubmitError("");
    try {
      setSaving(true);
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      if (mode === "edit") {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.ASSETS}/update`;
        // /assets/update reads clientId from a query param, not the path.
        const res = await axios.put(url, {...asset, ...form}, {params: {clientId: getClientIdOfUser()}});
        // Trust the server's echo over an optimistic merge — a backend that
        // predates a field (e.g. vehicleType) silently drops it and returns
        // 200; the echo is what the DB actually holds.
        const saved = res?.data?.responseData || null;
        if (saved && form.vehicleType && saved.vehicleType !== form.vehicleType) {
          console.warn(
            "[AssetSlideOver] Server did not persist vehicleType — sent",
            form.vehicleType, "echoed", saved.vehicleType,
            "(backend likely predates client_asset.vehicle_type / changeset 036)"
          );
          showToast("Saved, but the server ignored the vehicle segment — backend update required.", {type: "warning"});
        } else {
          showToast("Asset updated", {type: "success"});
        }
        onSaved && onSaved(saved ? {...asset, ...saved} : {...asset, ...form});
      } else {
        // Single-asset registration: POST /api/v1/{clientId}/assets with the
        // ApiRequest<ClientAssetRequest> envelope — the asset goes inside a
        // `clientAsset` wrapper alongside the (MOBI_TRACK-only) countryCode.
        const companyId = clientId ?? getClientIdOfUser();
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${companyId}/${UrlConstants.ASSETS}`;
        const body = registerAssetBody({
          form,
          clientId: companyId,
          userId: getLoggedInUserId(),
          requestId: generateRandomNumber(),
        });
        const res = await axios.post(url, body);
        // Some rejections come back as HTTP 200 with a non-success responseCode
        // (e.g. "phone linked to another client") — surface those as errors too.
        const rc = res?.data?.responseCode;
        if (rc && rc !== "0000" && rc !== "200") {
          const emsg = res?.data?.responseData || res?.data?.responseMessage || "Could not register asset.";
          const text = typeof emsg === "string" ? emsg : "Could not register asset.";
          setSubmitError(text);
          showToast(text, {type: "error", autoClose: 6000, position: "top-center"});
          return;
        }
        showToast("Asset registered", {type: "success"});
        onSaved && onSaved(res?.data?.responseData || null);
      }
      onClose && onClose();
    } catch (err) {
      const raw =
        err?.response?.data?.responseData ||
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        "Could not save asset.";
      const text = typeof raw === "string" ? raw : "Could not save asset.";
      setSubmitError(text);
      showToast(text, {type: "error", autoClose: 6000, position: "top-center"});
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "edit" ? "Edit asset" : "Register asset";
  const subtitle = mode === "edit" && asset
    ? `${asset.name} · ID ${asset.assetId}`
    : clientName
      ? `For ${clientName}`
      : "Add a new tracker or lock to the system.";

  return (
    <SlideOver open={open} onOpenChange={(o) => !o && onClose && onClose()} title={title} subtitle={subtitle} size="md">
      <SlideOver.Body>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Asset name"
              required
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
              error={errors.name}
              placeholder="V L Logistic Elock"
              autoFocus={mode === "create"}
            />
            <TextField
              label="Unit ID"
              required
              value={form.assetUnitId}
              onChange={(e) => change("assetUnitId", e.target.value)}
              error={errors.assetUnitId}
              placeholder="6102232365"
              disabled={mode === "edit"}
              helper={mode === "edit" ? "Unit ID is permanent." : "Device serial / IMEI."}
            />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-ink-700">
                Asset type <span className="text-danger-600">*</span>
              </label>
              <Select
                value={form.assetType}
                onChange={(e) => change("assetType", e.target.value)}
                hasError={!!errors.assetType}
                disabled={mode === "edit"}
              >
                <option value="">Select type…</option>
                {ASSET_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
              {errors.assetType && <p className="text-xs text-danger-600">{errors.assetType}</p>}
            </div>
            {form.assetType === "GPS_LOCK" && (
              <TextField
                label="Device password"
                required={mode === "create"}
                type="password"
                value={form.assetPassword}
                onChange={(e) => change("assetPassword", e.target.value)}
                error={errors.assetPassword}
                placeholder="••••••"
                helper="6 characters — used to authenticate lock/unlock commands."
                autoComplete="new-password"
              />
            )}
            {form.assetType === "GT06_TRACKER" && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ink-700">Vehicle segment</label>
                <Select
                  value={form.vehicleType}
                  onChange={(e) => change("vehicleType", e.target.value)}
                >
                  <option value="">Not specified</option>
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
                <p className="text-xs text-ink-500">Picks the vehicle symbol shown on live maps.</p>
              </div>
            )}
            {form.assetType === "GT06_TRACKER" && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ink-700">Protocol</label>
                <Select
                  value={form.protocol}
                  onChange={(e) => change("protocol", e.target.value)}
                >
                  <option value="">GT06 (default)</option>
                  <option value="FT920">FT920 (JT808)</option>
                </Select>
                <p className="text-xs text-ink-500">
                  Wire protocol of the tracker hardware. FT920 devices support remote commands from
                  the Command Center; the value is also stamped automatically when the device first
                  connects.
                </p>
              </div>
            )}
            {form.assetType === "MOBI_TRACK" && mode === "create" && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-ink-700">
                  Country code <span className="text-danger-600">*</span>
                </label>
                <Select
                  value={form.countryCode}
                  onChange={(e) => change("countryCode", e.target.value)}
                  hasError={!!errors.countryCode}
                >
                  <option value="">Select country code…</option>
                  {COUNTRIES.map((c) => (
                    <option key={`${c.dialingCode}-${c.name}`} value={c.dialingCode}>
                      {c.dialingCode} ({c.name})
                    </option>
                  ))}
                </Select>
                {errors.countryCode
                  ? <p className="text-xs text-danger-600">{errors.countryCode}</p>
                  : <p className="text-xs text-ink-500">Dialling code of the tracked phone number.</p>}
              </div>
            )}
          </div>

          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => change("description", e.target.value)}
            placeholder="Optional notes about this asset"
          />

          {mode === "edit" && asset && (
            <p className="text-xs text-ink-500 pt-3 border-t border-ink-100">
              {registeredUpdatedTitle(asset.createdAt, asset.updatedAt)}
            </p>
          )}
        </div>
        {submitError && (
          <div
            role="alert"
            style={{
              margin: "12px 0 0",
              padding: "10px 12px",
              borderRadius: 8,
              background: "#fdecec",
              border: "1px solid #f5c2c2",
              color: "#b42318",
              fontSize: 13,
              lineHeight: 1.4,
            }}
          >
            {submitError}
          </div>
        )}
      </SlideOver.Body>
      <SlideOver.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>
          {mode === "edit" ? "Save changes" : "Register asset"}
        </Button>
      </SlideOver.Footer>
    </SlideOver>
  );
}
